import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import DataGrid, { Column, Scrolling, SearchPanel } from 'devextreme-react/data-grid';
import LoadIndicator from 'devextreme-react/load-indicator';
import Popup from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { showErrorToast, showSuccessToast } from '@jarvis/platform-ui';
import {
  createEmojiCatalogItem,
  deactivateEmojiCatalogItem,
  listEmojiCatalogAdmin,
  listEmojiCatalogAdminCategories,
  updateEmojiCatalogItem,
  type EmojiCatalogItem,
  type EmojiCatalogUpsertRequest
} from '../../../api/emojiCatalog';
import { EMOJI_CATEGORY_LABELS, EMOJI_EDITOR_CATEGORIES } from '../../Apps/Utilities/emojiCatalog';
import '../../../apps/utilityApps.css';
import './emojiLibraryAdmin.css';

const PAGE_SIZE = 50;

type EditorState = {
  mode: 'create' | 'edit';
  id?: string;
  emoji: string;
  name: string;
  category: string;
  keywords: string;
  isActive: boolean;
};

const emptyEditor = (): EditorState => ({
  mode: 'create',
  emoji: '',
  name: '',
  category: 'faces',
  keywords: '',
  isActive: true
});

function parseKeywords(raw: string): string[] {
  return raw
    .split(/[,;\n]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

function categoryLabel(name: string): string {
  return EMOJI_CATEGORY_LABELS.get(name as never) ?? name;
}

export function EmojiLibraryAdminPage() {
  const [items, setItems] = useState<EmojiCatalogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [category, setCategory] = useState('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState>(emptyEditor);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [q]);

  const reloadCategories = useCallback(async () => {
    try {
      const cats = await listEmojiCatalogAdminCategories();
      const known = EMOJI_EDITOR_CATEGORIES.map((c) => c.id);
      const extras = cats.map((c) => c.name).filter((name) => !known.includes(name as never));
      setCategories([...known, ...extras]);
    } catch {
      setCategories(EMOJI_EDITOR_CATEGORIES.map((c) => c.id));
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const page = await listEmojiCatalogAdmin({
        q: debouncedQ || undefined,
        category: category === 'all' ? undefined : category,
        skip,
        take: PAGE_SIZE
      });
      setItems(page.items);
      setTotal(page.total);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not load emoji library.');
    } finally {
      setLoading(false);
    }
  }, [category, debouncedQ, skip]);

  useEffect(() => {
    void reloadCategories();
  }, [reloadCategories]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const categoryChips = useMemo(() => ['all', ...categories], [categories]);

  function openCreate() {
    setEditor(emptyEditor());
    setEditorOpen(true);
  }

  function openEdit(row: EmojiCatalogItem) {
    setEditor({
      mode: 'edit',
      id: row.id,
      emoji: row.emoji,
      name: row.name,
      category: row.category,
      keywords: (row.keywords ?? []).join(', '),
      isActive: row.isActive
    });
    setEditorOpen(true);
  }

  async function saveEditor() {
    const body: EmojiCatalogUpsertRequest = {
      emoji: editor.emoji.trim(),
      name: editor.name.trim(),
      category: editor.category.trim(),
      keywords: parseKeywords(editor.keywords),
      isActive: editor.isActive
    };
    if (!body.emoji || !body.name || !body.category) {
      showErrorToast('Emoji, name, and category are required.');
      return;
    }

    setBusy(true);
    try {
      if (editor.mode === 'create') {
        await createEmojiCatalogItem(body);
        showSuccessToast('Emoji added to the catalog.');
      } else if (editor.id) {
        await updateEmojiCatalogItem(editor.id, body);
        showSuccessToast('Emoji updated.');
      }
      setEditorOpen(false);
      await reloadCategories();
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not save emoji.');
    } finally {
      setBusy(false);
    }
  }

  async function onDeactivate(row: EmojiCatalogItem) {
    setBusy(true);
    try {
      await deactivateEmojiCatalogItem(row.id);
      showSuccessToast('Emoji deactivated.');
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not deactivate emoji.');
    } finally {
      setBusy(false);
    }
  }

  const pageIndex = Math.floor(skip / PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="ops-fill-page emoji-library-admin">
      <div className="ops-fill-page__header">
        <div>
          <h1 className="utility-app__title" style={{ margin: 0 }}>
            Emoji Library
          </h1>
          <p className="utility-app__muted" style={{ margin: '0.25rem 0 0' }}>
            Seeded workplace preset is insert-only. Add or edit items here without reseeding.
          </p>
        </div>
        <div className="utility-app__toolbar-group">
          {loading || busy ? <LoadIndicator visible height={18} width={18} /> : null}
          <Button text="Add item" type="default" icon="plus" onClick={openCreate} />
          <Button text="Refresh" icon="refresh" stylingMode="outlined" onClick={() => void reload()} />
        </div>
      </div>

      <div className="ops-fill-page__panel emoji-library-admin__panel">
        <div className="emoji-library-admin__filters">
          <div className="emoji-kb__cats" role="tablist" aria-label="Filter by category">
            {categoryChips.map((name) => (
              <Button
                key={name}
                text={name === 'all' ? 'All' : categoryLabel(name)}
                stylingMode={category === name ? 'contained' : 'outlined'}
                type={category === name ? 'default' : 'normal'}
                onClick={() => {
                  setSkip(0);
                  setCategory(name);
                }}
              />
            ))}
          </div>
          <TextBox
            value={q}
            width="100%"
            mode="search"
            placeholder="Search name, emoji, keywords…"
            showClearButton
            onValueChanged={(e) => {
              setSkip(0);
              setQ(String(e.value ?? ''));
            }}
          />
          <div className="emoji-library-admin__pager">
            <span className="utility-app__muted">
              Showing {items.length ? skip + 1 : 0}–{skip + items.length} of {total}
            </span>
            <div className="emoji-library-admin__pager-actions">
              <Button
                text="Previous"
                stylingMode="outlined"
                disabled={pageIndex <= 0 || loading}
                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              />
              <span className="utility-app__muted">
                Page {pageIndex + 1} / {pageCount}
              </span>
              <Button
                text="Next"
                stylingMode="outlined"
                disabled={pageIndex + 1 >= pageCount || loading}
                onClick={() => setSkip(skip + PAGE_SIZE)}
              />
            </div>
          </div>
        </div>

        <div className="ops-fill-page__grid-host">
          <DataGrid
            dataSource={items}
            keyExpr="id"
            showBorders
            rowAlternationEnabled
            hoverStateEnabled
            height="100%"
            columnAutoWidth
            wordWrapEnabled
            paging={{ enabled: false }}
            onRowDblClick={(e) => {
              if (e.data) openEdit(e.data as EmojiCatalogItem);
            }}
          >
            <Scrolling mode="standard" showScrollbar="always" useNative />
            <SearchPanel visible={false} />
            <Column
              caption="Emoji"
              width={90}
              allowSorting={false}
              cellRender={(cell) => (
                <span className="emoji-library-admin__glyph">{(cell.data as EmojiCatalogItem).emoji}</span>
              )}
            />
            <Column dataField="name" caption="Name" minWidth={180} />
            <Column
              dataField="category"
              caption="Category"
              width={160}
              calculateDisplayValue={(row: EmojiCatalogItem) => categoryLabel(row.category)}
            />
            <Column
              caption="Keywords"
              minWidth={180}
              allowSorting={false}
              calculateCellValue={(row: EmojiCatalogItem) => (row.keywords ?? []).join(', ')}
            />
            <Column dataField="isActive" caption="Active" width={80} dataType="boolean" />
            <Column dataField="origin" caption="Origin" width={90} />
            <Column
              caption="Actions"
              width={180}
              allowSorting={false}
              cellRender={(cell) => {
                const row = cell.data as EmojiCatalogItem;
                return (
                  <div className="emoji-library-admin__actions">
                    <Button text="Edit" stylingMode="text" onClick={() => openEdit(row)} />
                    {row.isActive ? (
                      <Button
                        text="Deactivate"
                        stylingMode="text"
                        type="danger"
                        onClick={() => void onDeactivate(row)}
                      />
                    ) : null}
                  </div>
                );
              }}
            />
          </DataGrid>
        </div>
      </div>

      <Popup
        visible={editorOpen}
        onHiding={() => setEditorOpen(false)}
        dragEnabled
        showCloseButton
        title={editor.mode === 'create' ? 'Add emoji' : 'Edit emoji'}
        width={520}
        height="auto"
      >
        <div className="emoji-library-admin__editor">
          <TextBox
            value={editor.emoji}
            label="Emoji"
            stylingMode="outlined"
            onValueChanged={(e) => setEditor((prev) => ({ ...prev, emoji: String(e.value ?? '') }))}
          />
          <TextBox
            value={editor.name}
            label="Name"
            stylingMode="outlined"
            onValueChanged={(e) => setEditor((prev) => ({ ...prev, name: String(e.value ?? '') }))}
          />
          <SelectBox
            value={editor.category}
            label="Category"
            stylingMode="outlined"
            dataSource={EMOJI_EDITOR_CATEGORIES}
            valueExpr="id"
            displayExpr="label"
            searchEnabled
            acceptCustomValue
            onValueChanged={(e) =>
              setEditor((prev) => ({ ...prev, category: String(e.value ?? 'faces') }))
            }
            onCustomItemCreating={(e) => {
              const value = String(e.text ?? '').trim() || 'faces';
              e.customItem = { id: value, label: value };
            }}
          />
          <TextBox
            value={editor.keywords}
            label="Keywords (comma-separated)"
            stylingMode="outlined"
            onValueChanged={(e) =>
              setEditor((prev) => ({ ...prev, keywords: String(e.value ?? '') }))
            }
          />
          <CheckBox
            value={editor.isActive}
            text="Active"
            onValueChanged={(e) =>
              setEditor((prev) => ({ ...prev, isActive: Boolean(e.value) }))
            }
          />
          <div className="emoji-library-admin__editor-actions">
            <Button text="Cancel" stylingMode="outlined" onClick={() => setEditorOpen(false)} />
            <Button text="Save" type="default" disabled={busy} onClick={() => void saveEditor()} />
          </div>
        </div>
      </Popup>
    </div>
  );
}
